function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { ProfileContext } from 'context';
import { useAction } from 'hooks';
import Account from 'modules/Account';
import CloudDrive from 'modules/CloudDrive';
import Hcp from 'modules/Hcp';
import Notifications from 'modules/Notifications';
import Statistics from 'modules/Statistics';
import * as selectors from '../../../../selectors';
import { useStandards } from '../../../../hooks';
export const useStoreReadings = (patientProfile, onCancel)=>{
    const profileContext = useContext(ProfileContext);
    const deviceData = useSelector(selectors.deviceData);
    const account = useSelector(Account.selectors.account);
    const activeProfileType = useSelector(Account.selectors.activeProfileType);
    const isHcp = activeProfileType === Account.constants.PROFILE_TYPES.HCP;
    const activeClinicMembership = useSelector(Account.selectors.activeClinicMembership);
    const passphrase = useSelector(Account.selectors.passphrase);
    const isStoreReadingsInProgress = useSelector(CloudDrive.selectors.isStoreReadingsInProgress);
    const hasStoreReadingsErrors = useSelector(CloudDrive.selectors.hasStoreReadingsErrors);
    const phiSet = useSelector(profileContext.selectors.phiSet);
    const phiSetDocumentId = useSelector(profileContext.selectors.phiSetDocumentId);
    const { storeReadingsSuccess } = profileContext.actions;
    const standards = useStandards();
    const totalReadingsCount = useRef(0);
    const prevIsStoreReadingsInProgressRef = useRef(isStoreReadingsInProgress);
    const { id: phiSetId } = phiSet || {};
    const phiSetIdRef = useRef(phiSetId);
    const [isInProgress, setIsInProgress] = useState(false);
    const sendClinicPatientNewReadingsAddedNotification = useAction(Notifications.actions.sendClinicPatientNewReadingsAddedNotification);
    const updateProfile = useAction(Hcp.actions.updatePatient, patientProfile);
    const storeReadings = useAction(CloudDrive.actions.storeReadings, deviceData, phiSet, phiSetDocumentId, patientProfile, storeReadingsSuccess);
    const sendStatistics = useAction(Statistics.actions.sendStatistics);
    const sendStatisticsForClinic = useAction(Statistics.actions.sendStatisticsForClinic);
    const onImport = ()=>{
        setIsInProgress(true);
        if (isHcp) {
            const { deviceSerialNumberToken } = deviceData;
            const serialNumberTokens = get(patientProfile, 'devicesSerialNumberTokens') || [];
            if (!includes(serialNumberTokens, deviceSerialNumberToken)) {
                const devicesSerialNumberTokens = [
                    ...serialNumberTokens,
                    deviceSerialNumberToken
                ];
                updateProfile({
                    devicesSerialNumberTokens
                }, activeClinicMembership);
            }
        }
        storeReadings();
    };
    const onSendNewReadingsNotification = (readingsCount)=>{
        const { clinicId } = activeClinicMembership.clinic;
        const { id, firstName, lastName, hcpProfileId } = patientProfile;
        sendClinicPatientNewReadingsAddedNotification({
            clinicId,
            hcpProfileId,
            patientFirstName: firstName,
            patientLastName: lastName,
            localPatientProfileId: id,
            readingsCount
        });
    };
    const onSendStats = ()=>{
        if (!isHcp) {
            return sendStatistics(_object_spread({}, patientProfile, account), passphrase, phiSet, deviceData, standards);
        }
        return sendStatisticsForClinic(patientProfile, phiSet, deviceData, standards, activeClinicMembership);
    };
    useEffect(()=>{
        if (!phiSetIdRef.current && phiSetId) {
            totalReadingsCount.current = get(phiSet, 'summaryData.totalReadingsCount', 0);
        }
        phiSetIdRef.current = phiSetId;
    }, [
        phiSetId
    ]);
    useEffect(()=>{
        totalReadingsCount.current = get(phiSet, 'summaryData.totalReadingsCount', 0);
    }, []);
    useEffect(()=>{
        if (isInProgress && prevIsStoreReadingsInProgressRef.current !== isStoreReadingsInProgress) {
            if (!isStoreReadingsInProgress) {
                if (!hasStoreReadingsErrors) {
                    const updatedTotalReadingsCount = get(phiSet, 'summaryData.totalReadingsCount', 0);
                    if (isHcp && updatedTotalReadingsCount > totalReadingsCount.current) {
                        onSendNewReadingsNotification(updatedTotalReadingsCount - totalReadingsCount.current);
                    }
                    onSendStats();
                    onCancel();
                } else {
                    setIsInProgress(false);
                }
            }
            prevIsStoreReadingsInProgressRef.current = isStoreReadingsInProgress;
        }
    }, [
        isStoreReadingsInProgress
    ]);
    return {
        isInProgress,
        onImport
    };
};
